import React from 'react';
import { Link } from 'react-router-dom';
import env from '../../../environment.js';

// Layout (alignment, gap, wrapping) is owned by ListToolbar, not the buttons.
// Each button only styles itself (color, radius, opacity) and takes its
// natural width, so the toolbar looks consistent with 1 button or with 4.
const btnNuevoStyle = {
  borderRadius: '20px',
  color: '#333',
  fontWeight: 'normal',
  fontSize: 14,
  lineHeight: '24px',
  backgroundColor: env.colors.secondary_200,
};

const btnSecundarioStyle = disabled => ({
  borderRadius: '20px',
  fontSize: 14,
  lineHeight: '24px',
  opacity: disabled ? 0.4 : 1,
});

const btnEliminarStyle = disabled => ({
  borderColor: 'red',
  borderRadius: '20px',
  fontSize: 14,
  lineHeight: '24px',
  opacity: disabled ? 0.25 : 1,
});

export const BtnNuevo = ({ onClick, to, children, show = true, disabled = false, icon = 'fa-plus', minWidth }) => {
  if (!show) return null;
  // icon can be set to null/false to render a filled primary button with no icon.
  const iconEl = icon ? <i className={`fa ${icon} pr-2`} /> : null;
  const style = { ...btnNuevoStyle, ...(minWidth ? { minWidth } : {}) };
  // A react-router Link can't be natively disabled; when a navigation target is
  // disabled we fall through to a plain disabled button so it greys out and
  // doesn't navigate. When enabled, onClick still runs on the Link (e.g. guards).
  if (to && !disabled) {
    return (
      <Link className="btn btn-sirio" to={to} onClick={onClick} style={style}>
        {iconEl}
        {children}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className="btn btn-sirio"
      onClick={to ? undefined : onClick}
      disabled={disabled}
      style={{ ...style, ...(disabled ? { opacity: 0.5 } : {}) }}>
      {iconEl}
      {children}
    </button>
  );
};

export const BtnSecundario = ({ onClick, children, disabled = false, icon = 'fa-edit', show = true, minWidth }) => {
  if (!show) return null;
  return (
    <button
      type="button"
      className="btn btn-outline-secondary btn-sirio"
      onClick={onClick}
      disabled={disabled}
      style={{ ...btnSecundarioStyle(disabled), ...(minWidth ? { minWidth } : {}) }}>
      <i className={`fa ${icon} pr-2`} />
      {children}
    </button>
  );
};

export const BtnEliminar = ({ onClick, children = 'Eliminar', disabled = false, show = true, minWidth }) => {
  if (!show) return null;
  return (
    <button
      type="button"
      className="btn"
      onClick={onClick}
      disabled={disabled}
      style={{ ...btnEliminarStyle(disabled), ...(minWidth ? { minWidth } : {}) }}>
      <span style={{ color: 'red' }}>
        <i className="fa fa-trash pr-2" /> {children}
      </span>
    </button>
  );
};

// Danger "confirm/cancel" toggle button (e.g. Cancelar alta / Cancelar baja in
// padron). Filled red when actionable, transparent outline when disabled.
export const BtnCancelar = ({ onClick, children, disabled = false, show = true }) => {
  if (!show) return null;
  return (
    <button
      type="button"
      className="btn btn-outline-secondary btn-sirio"
      onClick={onClick}
      disabled={disabled}
      style={{
        borderRadius: '20px',
        fontSize: 14,
        lineHeight: '24px',
        backgroundColor: disabled ? 'transparent' : '#93000A',
      }}>
      <span style={{ color: disabled ? '#333' : 'white' }}>{children}</span>
    </button>
  );
};

// Defaults applied to every toolbar so pages don't repeat layout props:
//  - single-line flex row that never wraps the actions to a second line
//  - uniform min-width on every action button (e.g. "Editar" as wide as "Nuevo")
// Opt out per page when a screen genuinely needs the old behaviour:
//  - allowWrap: fall back to the responsive col-based grid (leftCol/rightCol)
//    that wraps buttons onto a new line when space runs out
//  - buttonMinWidth={0} (or null): don't force a uniform button width
const DEFAULT_BUTTON_MIN_WIDTH = 140;

const ListToolbar = ({
  left,
  children,
  leftCol = 'col-5',
  rightCol = 'col-md-7',
  allowWrap = false,
  buttonMinWidth = DEFAULT_BUTTON_MIN_WIDTH,
}) => {
  // Inject the uniform min-width into each child that doesn't set its own, so a
  // page only decides the width once (or not at all) instead of on every button.
  const actions = buttonMinWidth
    ? React.Children.map(children, child =>
        React.isValidElement(child)
          ? React.cloneElement(child, { minWidth: child.props.minWidth ?? buttonMinWidth })
          : child
      )
    : children;

  // Default: single flex row that never wraps. The left area (search) shrinks
  // (minWidth: 0) so the buttons keep their width and stay on one line; on very
  // small screens the row overflows instead of breaking onto two lines.
  if (!allowWrap) {
    return (
      <div className="d-flex flex-nowrap align-items-center mt-3 mb-3" style={{ gap: '10px' }}>
        <div className="d-flex" style={{ flex: '1 1 auto', minWidth: 0 }}>
          {left}
        </div>
        <div
          className="d-flex flex-nowrap justify-content-end align-items-center"
          style={{ gap: '10px', flexShrink: 0 }}>
          {actions}
        </div>
      </div>
    );
  }
  // Opt-in fallback: responsive col-based grid that wraps actions when needed.
  return (
    <div className="row mt-3 align-items-center">
      <div className={`${leftCol} d-flex`}>{left}</div>
      <div
        className={`${rightCol} d-flex flex-wrap justify-content-end align-items-center mb-3`}
        style={{ gap: '10px' }}>
        {actions}
      </div>
    </div>
  );
};

export default ListToolbar;
